/**
 * Resident Financial Translation Module.
 * Ported from Python resident_financial_translator.py
 *
 * Converts brain health pathway scores into avoided-cost metrics for senior living residents.
 * Channels: Fall Prevention, Cognitive Preservation, Mood/Depression, Health/Hospitalization.
 *
 * Now supports acuity-tier differentiation (IL/AL/MC/SNF) with published baseline risk rates.
 */

// ── Acuity Tier Defaults ──
// Published baseline risk rates per care level (2025 USD)
// costPerCognitiveYear reflects INCREMENTAL environment-sensitive costs only
// (agitation management, wandering incidents, excess supervision, behavioral episodes)
// NOT total dementia care costs — environmental design cannot offset medical/pharmaceutical costs
// cognitiveImpairmentRate scoped to mild-moderate (environment-responsive) impairment
export const ACUITY_TIERS = {
  IL: {
    label: 'Independent Living',
    fallsPerYear: 0.65,       // Rubenstein 2006; CDC STEADI
    hospitalizationsPerYear: 0.12, // Medicare FFS data; OIG reports
    depressionRate: 0.08,     // Djernes 2006 systematic review
    cognitiveImpairmentRate: 0.08, // Subset with mild/environment-responsive MCI
    costPerFall: 9000,        // Florence 2018 adjusted to 2025$
    costPerHospitalization: 15600, // HCUP/Medicare 2017 adjusted
    costPerDepressionYear: 6000,   // Vasiliadis 2022; AHRQ/CMS
    costPerCognitiveYear: 12000,   // Incremental: excess supervision, behavioral management
  },
  AL: {
    label: 'Assisted Living',
    fallsPerYear: 1.75,       // Rubenstein 2006; CDC
    hospitalizationsPerYear: 0.30, // Medicare FFS; OIG
    depressionRate: 0.28,     // Djernes 2006; Seitz 2010
    cognitiveImpairmentRate: 0.30, // Mild-moderate subset of 50% total (Harris-Kojetin 2019)
    costPerFall: 18000,       // Florence 2018 adjusted
    costPerHospitalization: 15600,
    costPerDepressionYear: 6000,
    costPerCognitiveYear: 18000,   // Incremental: wandering, agitation, excess care hours
  },
  MC: {
    label: 'Memory Care',
    fallsPerYear: 3.0,        // Rubenstein 2006; elevated for dementia
    hospitalizationsPerYear: 0.40, // Medicare FFS
    depressionRate: 0.38,     // Seitz 2010; comorbid depression in dementia
    cognitiveImpairmentRate: 0.60, // Subset with environment-responsive behavioral symptoms
    costPerFall: 25000,       // Florence 2018 adjusted; higher injury severity
    costPerHospitalization: 15600,
    costPerDepressionYear: 6000,
    costPerCognitiveYear: 20000,   // Incremental: behavioral episodes, excess 1:1 care
  },
  SNF: {
    label: 'Skilled Nursing',
    fallsPerYear: 2.0,        // Rubenstein 2006; CDC
    hospitalizationsPerYear: 0.62, // OIG Medicare SNF readmission data
    depressionRate: 0.35,     // Djernes 2006
    cognitiveImpairmentRate: 0.30, // Mild-moderate subset of 48% total (Harris-Kojetin 2019)
    costPerFall: 20000,       // Florence 2018 adjusted
    costPerHospitalization: 15600,
    costPerDepressionYear: 6000,
    costPerCognitiveYear: 16000,   // Incremental: behavioral management, excess supervision
  },
};

// ── Evidence Citations ──
export const RESIDENT_EVIDENCE = {
  fallPrevention: [
    { author: 'Rubenstein', year: 2006, finding: 'Fall rates by care setting: IL 0.65/yr, AL 1.75/yr, MC 3.0/yr, SNF 2.0/yr', channel: 'Fall Prevention' },
    { author: 'Florence et al.', year: 2018, finding: 'Medical cost per fall injury $9,780 (2015$); adjusted to 2025$ by tier severity', channel: 'Fall Prevention' },
    { author: 'Sherrington et al.', year: 2019, finding: 'Exercise reduces fall rate by 23%; env. design attribution ~12% (layout encouraging movement, lighting, wayfinding)', channel: 'Fall Prevention' },
    { author: 'CDC STEADI', year: 2024, finding: 'Multifactorial fall prevention includes lighting, flooring, wayfinding; env. design is one factor among staffing, medication review, exercise programs', channel: 'Fall Prevention' },
  ],
  cognitivePreservation: [
    { author: 'Plassman et al.', year: 2007, finding: 'Cognitive impairment prevalence: 12% age 71+; rises with care level', channel: 'Cognitive Preservation' },
    { author: 'Harris-Kojetin et al.', year: 2019, finding: 'NSLTCP: 50% AL, 48% SNF residents have cognitive impairment', channel: 'Cognitive Preservation' },
    { author: 'Hurd et al.', year: 2013, finding: 'Total dementia care $56K-$82K/yr; incremental environment-sensitive costs (agitation, wandering, excess supervision) estimated at $12K-$20K/yr', channel: 'Cognitive Preservation' },
    { author: 'Cochrane CST Review', year: 2023, finding: 'Enriched environments delay cognitive decline ~6 months; 3% env. design attribution (of 8% total reduction) after discounting for non-environmental factors', channel: 'Cognitive Preservation' },
  ],
  moodDepression: [
    { author: 'Djernes', year: 2006, finding: 'Depression prevalence: 8% IL, 28% AL, 38% MC, 35% SNF', channel: 'Mood / Depression' },
    { author: 'Seitz et al.', year: 2010, finding: 'Comorbid depression in dementia: 38% in MC settings', channel: 'Mood / Depression' },
    { author: 'Vasiliadis et al.', year: 2022, finding: 'Incremental depression treatment cost: $5,752-$6,266/yr', channel: 'Mood / Depression' },
    { author: 'de Vries et al.', year: 2015, finding: 'Nature exposure reduces depression 30-40%; 20% max reduction conservative', channel: 'Mood / Depression' },
  ],
  healthHospitalization: [
    { author: 'OIG/CMS', year: 2023, finding: 'SNF 30-day hospital readmission rate: 62%; Medicare FFS data', channel: 'Hospitalization' },
    { author: 'HCUP/Medicare', year: 2017, finding: 'Average cost per hospital admission $15,600 (2025$)', channel: 'Hospitalization' },
    { author: 'Ulrich', year: 1984, finding: 'Nature views reduce post-surgical hospital stays; basis for environmental design benefit', channel: 'Hospitalization' },
    { author: 'Joseph et al.', year: 2016, finding: 'Evidence-based design review: environmental quality reduces adverse health events', channel: 'Hospitalization' },
  ],
};

// ── Max reduction rates by channel ──
// These represent the ENVIRONMENTAL DESIGN PORTION ONLY of total possible reduction.
// Environmental features (lighting, acoustics, nature, layout) are one contributor
// among many (staffing, medication, programming, family support, individual health).
// Rates are discounted from literature ceilings to reflect partial attribution.
const MAX_REDUCTIONS = {
  falls: 0.12,          // Sherrington 2019 literature ceiling 23%; env. attribution ~50%
  cognitive: 0.03,      // Cochrane CST ceiling 8%; env. design share ~35-40%
  depression: 0.10,     // de Vries 2015 ceiling 20-30%; env. attribution ~40-50%
  hospitalization: 0.04, // Joseph 2016; limited direct evidence for env. → hospitalization
};

// ── Pathway composite scoring functions ──

function fallPreventionScore(pathwayScores) {
  const movement = Math.max(0, pathwayScores.movement || 0);
  const daylight = Math.max(0, pathwayScores.daylight || 0);
  const wayfinding = Math.max(0, pathwayScores.wayfinding || 0);
  return movement * 0.40 + daylight * 0.35 + wayfinding * 0.25;
}

function cognitivePreservationScore(pathwayScores) {
  const enrichment = Math.max(0, pathwayScores.enrichment || 0);
  const wayfinding = Math.max(0, pathwayScores.wayfinding || 0);
  const movement = Math.max(0, pathwayScores.movement || 0);
  return enrichment * 0.40 + wayfinding * 0.35 + movement * 0.25;
}

function moodDepressionScore(pathwayScores) {
  const nature = Math.max(0, pathwayScores.nature || 0);
  const enrichment = Math.max(0, pathwayScores.enrichment || 0);
  const daylight = Math.max(0, pathwayScores.daylight || 0);
  return nature * 0.35 + enrichment * 0.35 + daylight * 0.30;
}

function healthHospitalizationScore(pathwayScores) {
  const air = Math.max(0, 1.0 + (pathwayScores.air_quality || 0));
  const noise = Math.max(0, 1.0 + (pathwayScores.noise || 0));
  const visual = Math.max(0, 1.0 + (pathwayScores.visual_stress || 0));
  return air * 0.40 + noise * 0.35 + visual * 0.25;
}

/**
 * Calculate resident avoided-cost ROI by acuity tier.
 * @param {number} brainHealthScore - Overall score 0-1
 * @param {Object} pathwayScores - Dict of 8 pathway scores
 * @param {Object} tierData - { IL: { headcount, ...overrides }, AL: {...}, MC: {...}, SNF: {...} }
 * @returns {Object} Per-tier results, channel totals, and combined summary
 */
export function calculateResidentRoiByTier(brainHealthScore, pathwayScores, tierData) {
  const fallPct = fallPreventionScore(pathwayScores);
  const cognitivePct = cognitivePreservationScore(pathwayScores);
  const moodPct = moodDepressionScore(pathwayScores);
  const healthPct = healthHospitalizationScore(pathwayScores);

  const tierResults = {};
  let totalFalls = 0;
  let totalCognitive = 0;
  let totalMood = 0;
  let totalHealth = 0;
  let totalHeadcount = 0;

  for (const [tierKey, defaults] of Object.entries(ACUITY_TIERS)) {
    const data = tierData[tierKey] || {};
    const hc = Number(data.headcount) || 0;
    if (hc === 0) {
      tierResults[tierKey] = {
        label: defaults.label,
        headcount: 0,
        fallValue: 0, cognitiveValue: 0, moodValue: 0, healthValue: 0, total: 0,
      };
      continue;
    }

    totalHeadcount += hc;

    // Merge overrides with defaults
    const fallsPerYear = Number(data.fallsPerYear) || defaults.fallsPerYear;
    const costPerFall = Number(data.costPerFall) || defaults.costPerFall;
    const hospPerYear = Number(data.hospitalizationsPerYear) || defaults.hospitalizationsPerYear;
    const costPerHosp = Number(data.costPerHospitalization) || defaults.costPerHospitalization;
    const depressionRate = Number(data.depressionRate) || defaults.depressionRate;
    const costPerDepression = Number(data.costPerDepressionYear) || defaults.costPerDepressionYear;
    const cogRate = Number(data.cognitiveImpairmentRate) || defaults.cognitiveImpairmentRate;
    const costPerCognitive = Number(data.costPerCognitiveYear) || defaults.costPerCognitiveYear;

    // Per-channel: headcount * rate * costPerEvent * maxReduction * pathwayScore
    const fallValue = hc * fallsPerYear * costPerFall * MAX_REDUCTIONS.falls * fallPct;
    const cognitiveValue = hc * cogRate * costPerCognitive * MAX_REDUCTIONS.cognitive * cognitivePct;
    const moodValue = hc * depressionRate * costPerDepression * MAX_REDUCTIONS.depression * moodPct;
    const healthValue = hc * hospPerYear * costPerHosp * MAX_REDUCTIONS.hospitalization * healthPct;

    const tierTotal = fallValue + cognitiveValue + moodValue + healthValue;

    tierResults[tierKey] = {
      label: defaults.label,
      headcount: hc,
      fallValue: Math.round(fallValue * 100) / 100,
      cognitiveValue: Math.round(cognitiveValue * 100) / 100,
      moodValue: Math.round(moodValue * 100) / 100,
      healthValue: Math.round(healthValue * 100) / 100,
      total: Math.round(tierTotal * 100) / 100,
    };

    totalFalls += fallValue;
    totalCognitive += cognitiveValue;
    totalMood += moodValue;
    totalHealth += healthValue;
  }

  const grandTotal = totalFalls + totalCognitive + totalMood + totalHealth;

  const channels = [
    { name: 'Fall Prevention', annualSavings: Math.round(totalFalls * 100) / 100, avoidedCost: Math.round(totalFalls * 100) / 100, pct: fallPct },
    { name: 'Cognitive Preservation', annualSavings: Math.round(totalCognitive * 100) / 100, avoidedCost: Math.round(totalCognitive * 100) / 100, pct: cognitivePct },
    { name: 'Mood / Depression', annualSavings: Math.round(totalMood * 100) / 100, avoidedCost: Math.round(totalMood * 100) / 100, pct: moodPct },
    { name: 'Hospitalization', annualSavings: Math.round(totalHealth * 100) / 100, avoidedCost: Math.round(totalHealth * 100) / 100, pct: healthPct },
  ];

  return {
    overallBrainHealthScore: Math.round(brainHealthScore * 10000) / 10000,
    tierResults,
    channels,
    totalHeadcount,
    totalAnnualAvoidedCost: Math.round(grandTotal * 100) / 100,
    totalAvoidedCost: Math.round(grandTotal * 100) / 100,
    totalAnnualSavings: Math.round(grandTotal * 100) / 100,
    perResidentAnnualValue: totalHeadcount > 0 ? Math.round((grandTotal / totalHeadcount) * 100) / 100 : 0,
  };
}

/**
 * Legacy: Calculate resident avoided-cost ROI from brain health assessment.
 * Kept for backward compatibility — wraps calculateResidentRoiByTier with all headcount in IL.
 * @param {number} brainHealthScore - Overall score 0-1
 * @param {Object} pathwayScores - Dict of 8 pathway scores
 * @param {Object} residentData - { headcount, ... }
 * @returns {Object} ResidentFinancialResult
 */
export function calculateResidentRoi(brainHealthScore, pathwayScores, residentData) {
  const headcount = Number(residentData.headcount) || 0;

  if (headcount === 0) {
    return {
      overallBrainHealthScore: brainHealthScore,
      fallPreventionPct: 0, fallPreventionValue: 0,
      cognitivePreservationPct: 0, cognitivePreservationValue: 0,
      moodDepressionPct: 0, moodDepressionValue: 0,
      healthHospitalizationPct: 0, healthHospitalizationValue: 0,
      totalAnnualAvoidedCost: 0, totalAvoidedCost: 0, totalAnnualSavings: 0,
      perResidentAnnualValue: 0,
      channels: [],
    };
  }

  // Delegate to tier system — put all headcount in IL tier
  const tierData = {
    IL: { headcount, ...residentData },
    AL: { headcount: 0 },
    MC: { headcount: 0 },
    SNF: { headcount: 0 },
  };

  const result = calculateResidentRoiByTier(brainHealthScore, pathwayScores, tierData);

  // Also return legacy flat fields for backward compat
  const fallPct = fallPreventionScore(pathwayScores);
  const cognitivePct = cognitivePreservationScore(pathwayScores);
  const moodPct = moodDepressionScore(pathwayScores);
  const healthPct = healthHospitalizationScore(pathwayScores);

  return {
    ...result,
    fallPreventionPct: Math.round(fallPct * 10000) / 10000,
    fallPreventionValue: result.tierResults.IL.fallValue,
    cognitivePreservationPct: Math.round(cognitivePct * 10000) / 10000,
    cognitivePreservationValue: result.tierResults.IL.cognitiveValue,
    moodDepressionPct: Math.round(moodPct * 10000) / 10000,
    moodDepressionValue: result.tierResults.IL.moodValue,
    healthHospitalizationPct: Math.round(healthPct * 10000) / 10000,
    healthHospitalizationValue: result.tierResults.IL.healthValue,
  };
}
